/**
 * Passerelle MoMent → BoxyLab — le PROTOCOLE entre la page Nafis et l'extension
 * « Nafis Bridge » (21/10/2026). Types et constantes seulement : la page et
 * l'extension l'importent toutes deux, pour parler exactement la même langue.
 *
 * La page ouvre un port (`chrome.runtime.connect(idExtension, { name: PORT })`),
 * ce que `externally_connectable` réserve à https://nafis.bio24.tn. L'identifiant
 * de l'extension lui est ANNONCÉ par le petit script que l'extension pose sur les
 * pages Nafis (`ANNONCE`), ce qui évite de le figer dans le code de Nafis.
 */
import type { DemandeLue, Identite } from './boxylab-identite';

export const PORT = 'nafis-bridge';

/**
 * LES VERSIONS DE L'EXTENSION (21/10/2026). L'extension s'installe à la main (mode développeur) :
 * un poste peut rester sur une ancienne version sans le savoir.
 *  · DERNIÈRE : celle de `extensions/nafis-bridge/manifest.json` (un test le vérifie) — en dessous, Nafis
 *    invite à mettre à jour ;
 *  · MINIMALE : en dessous, Nafis REFUSE d'envoyer — à relever quand une version corrige un défaut de
 *    sûreté (0.1.8 : « imprimer » avec l'id de la demande, refus du CR déjà déposé).
 */
export const VERSION_DERNIERE = '0.1.8';
export const VERSION_MINIMALE = '0.1.8';

/** Compare deux versions « a.b.c » : négatif si a < b, 0 si égales, positif si a > b. Illisible = la plus ancienne. */
export function comparerVersions(a: string, b: string): number {
  const p = (v: string) => (/^\d+(\.\d+)*$/.test(v) ? v.split('.').map(Number) : [-1]);
  const x = p(a); const y = p(b);
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = (x[i] ?? 0) - (y[i] ?? 0);
    if (d) return d;
  }
  return 0;
}

/** Ce que Nafis dit d'une version installée. */
export function avisVersion(installee: string): { niveau: 'ok' | 'mise-a-jour' | 'refus'; message: string | null } {
  const lien = 'https://gitlab.com/bio24/ged/-/tree/main/extensions/nafis-bridge/livraisons';
  if (comparerVersions(installee, VERSION_MINIMALE) < 0) return { niveau: 'refus', message: `L’extension Nafis Bridge installée (${installee || 'version inconnue'}) est trop ancienne : installez la ${VERSION_DERNIERE} avant d’envoyer (${lien}).` };
  if (comparerVersions(installee, VERSION_DERNIERE) < 0) return { niveau: 'mise-a-jour', message: `Une nouvelle version de l’extension Nafis Bridge est disponible (${VERSION_DERNIERE} ; installée : ${installee}) : ${lien}.` };
  return { niveau: 'ok', message: null };
}
/** Le message `window.postMessage` par lequel l'extension se signale à la page Nafis. */
export const ANNONCE = 'nafis-bridge:present';

/** Une fiche de document BoxyLab, réduite à ce qui sort de l'onglet BoxyLab. */
export type DocumentBoxy = { id: number; nom: string; impression: boolean; contentHash: string | null };

export type EtatBoxyLab = 'absent' | 'a-recharger' | 'non-connecte' | 'suspendu' | 'pret';

/* ── Page → extension ── */
export type Demande =
  | { id: number; type: 'etat' }
  | { id: number; type: 'lire'; code: string; nomFichier: string; sha256: string }
  | { id: number; type: 'deposer'; code: string; identite: Identite; nomFichier: string; sha256: string; pdf: string; doublonConfirme: boolean }
  | { id: number; type: 'verifierRetrait'; code: string; documentIdBoxy: number; sha256: string }
  | { id: number; type: 'ouvrir'; demandeId: number };

export type ReponseEtat = { versionExtension: string; boxylab: EtatBoxyLab; versionBoxyLab: string | null; message: string | null };
/** `dejaDepose` : n° du document qui porte déjà CE CR (même empreinte) → refus ; `doublon` : un autre PDF du dossier (même nom) → confirmation. */
export type ReponseLire = { demande: DemandeLue; documents: DocumentBoxy[]; doublon: boolean; dejaDepose: number | null; versionBoxyLab: string | null };
export type ReponseRetrait = { retire: boolean };
/** `ouvert` : la demande est affichée dans l'onglet BoxyLab ; sinon `raison` dit pourquoi (onglet sur un autre écran). */
export type ReponseOuvrir = { ouvert: boolean; raison: string | null };

/** Une étape du dépôt, rendue AU FIL de l'eau : la page la consigne aussitôt au registre de Nafis. */
export type Progres =
  | { etape: 'upload'; ok: true; documentIdBoxy: number; contentHashBoxy: string }
  | { etape: 'impression'; ok: true }
  | { etape: 'verification'; ok: true; present: boolean; impression: boolean; hashIdentique: boolean };

/* ── Extension → page ── */
export type Message =
  | { id: number; type: 'progres'; progres: Progres }
  | { id: number; type: 'fin'; ok: true; resultat: unknown }
  | { id: number; type: 'fin'; ok: false; erreur: string; etape?: 'upload' | 'impression' | 'verification' | null };

/* ── Service worker → onglet BoxyLab (script de contenu) ── */
export type Operation =
  | { op: 'etat' }
  | { op: 'lire'; code: string }
  | { op: 'upload'; demandeId: number; patientId: number; nomFichier: string; pdf: string }
  | { op: 'sauver'; documentId: number; demandeId: number }
  | { op: 'oublier' };

export type ResultatOp<T> = { ok: true; valeur: T } | { ok: false; erreur: string };
